import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import { LayoutSettings } from "../../../../boxes/LayoutSettings/LayoutSettings";
import classes from "./scss/repeatMd.module.scss";
import { EntityHeader } from "../../../../widgets/EntityHeader/EntityHeader";
import { Toggle } from "../../../../shared/Toggle/Toggle";
import { useAppTranslation } from "../../../../i18n/useAppTranslation";
import { Box } from "../../../../shared/Box/Box";
import { useRepeatMdIntegration } from "./hooks/useRepeatMdIntegration";
import { useSaveRepeatMdIntegration } from "./hooks/useSaveRepeatMdIntegration";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import AuthorizationBox from "./components/AuthorizationBox";
import RepeatMdClinicSync from "./components/RepeatMdClinicSync";
import RepeatMdMembershipConfiguration from "./components/RepeatMdMembershipConfiguration";
import RepeatMdEnrollmentConfiguration from "./components/RepeatMdEnrollmentConfiguration";
import { integrationTypeOptions } from "./RepeatMd.consts";
import { InputLabel } from "../../../../shared/InputLabel/InputLabel";
import { Select } from "../../../../shared/Select/Select";
import UpgradeAccountModal from "../../../../Components/Common/UpgradeAccountModal";
import { getIsRepeatMdIntegrationAccessible } from "../../../../helpers/integrations";
import { uiNotification } from "../../../../services/UINotificationService";

const RepeatMd = () => {
  const { tSettings } = useAppTranslation.Settings();

  const queryClient = useQueryClient();

  const [showUpgradeModal, setShowUpgradeModal] = useState(
    !getIsRepeatMdIntegrationAccessible(),
  );

  const [expanded, setExpanded] = useState({
    clinicSync: false,
    membershipConfiguration: false,
    enrollmentConfiguration: false,
  });

  const { data, isLoading } = useRepeatMdIntegration({
    enabled: getIsRepeatMdIntegrationAccessible(),
  });

  const isEnabled = data?.isActive || false;
  const integrationType = data?.integrationType || null;
  const isLocationIntegration = integrationType === "location";

  const { mutate, isLoading: isSaving } = useSaveRepeatMdIntegration({
    onSuccess: (res) => {
      const data = res.data.data;
      queryClient.setQueryData(["repeatMdIntegration"], res);
      if (data.messages?.length > 0) {
        data.messages.forEach((message) => {
          uiNotification.success(message);
        });
      }
      if (data.errors?.length > 0) {
        data.errors.forEach((error) => {
          uiNotification.error(error);
        });
      }
    },
  });

  const isToggleDisabled = !getIsRepeatMdIntegrationAccessible();

  return (
    <LayoutSettings contentClassName={classes.root}>
      <EntityHeader
        title={tSettings("repeatMdIntegration.title")}
        right={
          <div className={isToggleDisabled && "cursor-not-allowed"}>
            {isSaving ? (
              <CircularProgress size="small" color="primary" />
            ) : (
              <Toggle
                isEnabled={isEnabled && getIsRepeatMdIntegrationAccessible()}
                onChange={() =>
                  mutate({
                    isActive: !isEnabled,
                  })
                }
                isDisabled={!getIsRepeatMdIntegrationAccessible()}
              />
            )}
          </div>
        }
      />
      {isEnabled && getIsRepeatMdIntegrationAccessible() && (
        <Box className={classes.clinicsBox}>
          {isLoading ? (
            <Skeleton count={5} height={"35px"} />
          ) : (
            <>
              <div className={classes.authorizationContainer}>
                <AuthorizationBox
                  updatedAt={data?.updatedAt}
                  data={data}
                  updateApiKey={mutate}
                  isUpdating={isSaving}
                />
                <div className={classes.integrationType}>
                  <InputLabel>
                    {tSettings("repeatMdIntegration.integrationType")}
                  </InputLabel>
                  <Select
                    size="small"
                    options={integrationTypeOptions}
                    value={integrationTypeOptions.find(
                      (integration) => integration.value === integrationType,
                    )}
                    isLoading={isSaving}
                    onChange={(option) => {
                      mutate({
                        isActive: true,
                        integrationType: option.value,
                        clinics: data?.clinics,
                      });
                    }}
                  />
                </div>
              </div>
              <div>
                {Boolean(data?.clinics.length) && isLocationIntegration && (
                  <>
                    <RepeatMdClinicSync
                      expanded={expanded}
                      setExpanded={setExpanded}
                      data={data}
                      mutate={mutate}
                      isSaving={isSaving}
                    />
                  </>
                )}
                <RepeatMdMembershipConfiguration
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
                <RepeatMdEnrollmentConfiguration
                  data={data}
                  expanded={expanded}
                  setExpanded={setExpanded}
                />
              </div>
            </>
          )}
        </Box>
      )}
      {!getIsRepeatMdIntegrationAccessible() && (
        <UpgradeAccountModal
          notificaionType="integration"
          isShowUpgradeAlertModal={showUpgradeModal}
          handleShowUpgradeAlertModal={() =>
            setShowUpgradeModal(!showUpgradeModal)
          }
        />
      )}
    </LayoutSettings>
  );
};

export default RepeatMd;
